import React, { memo, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import { images as imageApi } from '../../utils/api';

function GridContainer({ children }) {
  const animationProps = useMemo(() => ({
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 }
  }), []);

  return (
    <motion.div 
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6 p-3 sm:p-6"
      {...animationProps}
    >
      {children}
    </motion.div>
  );
}

const CoinImage = memo(({ image, isShared, year, denomination }) => {
  if (!image) {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
        <img 
          src="/images/placeholder.svg" 
          alt="No image available"
          className="w-16 h-16 opacity-50"
        />
      </div>
    );
  }

  return (
    <img
      src={imageApi.getImage(image.file_path, image.source)}
      alt={`${year} ${denomination}`}
      className="absolute inset-0 object-contain w-full h-full"
      loading="lazy"
    />
  );
});

function CoinCard({ coin, isShared, setInfo, onCoinClick }) {
  const handleClick = useCallback(() => {
    onCoinClick(coin);
  }, [coin, onCoinClick]);

  return (
    <motion.div
      className="relative group cursor-pointer bg-white rounded-lg shadow-lg overflow-hidden transform transition-all duration-300 hover:scale-105"
      whileHover={{ y: -5 }}
      onClick={handleClick}
    >
      <div className="relative pt-[100%] bg-gray-50">
        {Array.isArray(coin.images) && coin.images.some(img => img?.file_path === setInfo?.cover_image?.file_path) && (
          <div className="absolute top-2 right-2 z-10 bg-indigo-500 text-white text-xs px-2 py-1 rounded-full">
            Cover Image
          </div>
        )}
        <MemoizedCoinImage 
          image={coin.images?.[0]}
          isShared={isShared}
          year={coin.year}
          denomination={coin.denomination}
        />
      </div>
      <div className="p-4">
        <div className="space-y-1">
          <p className="text-sm text-gray-900 font-bold">
            {coin.country} {coin.year}{coin.mint_mark ? `-${coin.mint_mark}` : ''} {coin.assayer} {coin.denomination} {coin.variety}
          </p>
          {(setInfo?.field_preferences?.visible_fields?.includes('certification_service_id') || setInfo?.field_preferences?.visible_fields?.includes('grade')) && (
            <p className="text-xs text-gray-600">
              {setInfo?.field_preferences?.visible_fields?.includes('certification_service_id') && coin.certification_service_name}
              {setInfo?.field_preferences?.visible_fields?.includes('certification_service_id') && coin.certification_service_name && setInfo?.field_preferences?.visible_fields?.includes('grade') && coin.grade && ' '}
              {setInfo?.field_preferences?.visible_fields?.includes('grade') && coin.grade && coin.grade}
            </p>
          )}
          {setInfo?.field_preferences?.visible_fields?.includes('list_price') && coin.list_price && (
            <p className="text-xs text-gray-600">
              List Price: ${coin.list_price?.toLocaleString()}
            </p>
          )}
        </div>
      </div>
    </motion.div>
  );
}

function GridView({ 
  coins, 
  isShared, 
  setInfo,
  onCoinClick,
  loadingMore,
  hasMore,
  observerTarget
}) {
  if (!coins || coins.length === 0) {
    return (
      <div className="flex justify-center items-center min-h-[400px] text-gray-600">
        No coins available in this set
      </div>
    );
  }

  return (
    <>
      <MemoizedGridContainer>
        {coins.map((coin) => (
          <MemoizedCoinCard
            key={coin.id}
            coin={coin}
            isShared={isShared}
            setInfo={setInfo}
            onCoinClick={onCoinClick}
          />
        ))}
      </MemoizedGridContainer>
      {isShared && hasMore && (
        <div 
          ref={observerTarget}
          className="w-full py-4 flex justify-center"
        >
          {loadingMore && (
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          )}
        </div>
      )}
    </>
  );
};

// Custom equality function for GridView memo
const areEqual = (prevProps, nextProps) => {
  return (
    prevProps.coins === nextProps.coins &&
    prevProps.isShared === nextProps.isShared &&
    prevProps.setInfo === nextProps.setInfo &&
    prevProps.onCoinClick === nextProps.onCoinClick &&
    prevProps.loadingMore === nextProps.loadingMore &&
    prevProps.hasMore === nextProps.hasMore &&
    prevProps.observerTarget === nextProps.observerTarget
  );
};

// Custom equality function for CoinCard memo
const areCoinPropsEqual = (prevProps, nextProps) => {
  return (
    prevProps.coin.id === nextProps.coin.id &&
    prevProps.coin.country === nextProps.coin.country &&
    prevProps.coin.year === nextProps.coin.year &&
    prevProps.coin.mint_mark === nextProps.coin.mint_mark &&
    prevProps.coin.assayer === nextProps.coin.assayer &&
    prevProps.coin.denomination === nextProps.coin.denomination &&
    prevProps.coin.variety === nextProps.coin.variety &&
    prevProps.coin.certification_service_name === nextProps.coin.certification_service_name &&
    prevProps.coin.grade === nextProps.coin.grade &&
    prevProps.coin.list_price === nextProps.coin.list_price &&
    prevProps.isShared === nextProps.isShared &&
    (prevProps.coin.images?.[0]?.file_path ?? null) === (nextProps.coin.images?.[0]?.file_path ?? null) &&
    prevProps.setInfo?.cover_image?.file_path === nextProps.setInfo?.cover_image?.file_path &&
    prevProps.setInfo?.field_preferences?.visible_fields === nextProps.setInfo?.field_preferences?.visible_fields &&
    prevProps.onCoinClick === nextProps.onCoinClick
  );
};

// Custom equality function for CoinImage memo
const areImagePropsEqual = (prevProps, nextProps) => {
  return (
    prevProps.image?.file_path === nextProps.image?.file_path &&
    prevProps.isShared === nextProps.isShared &&
    prevProps.year === nextProps.year &&
    prevProps.denomination === nextProps.denomination
  );
};

const MemoizedGridContainer = memo(GridContainer);
const MemoizedCoinCard = memo(CoinCard, areCoinPropsEqual);
const MemoizedCoinImage = memo(CoinImage, areImagePropsEqual);
const MemoizedGridView = memo(GridView, areEqual);

export default MemoizedGridView;
